import { Cancel } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  ChipProps,
  Grid,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

// TODO(filters-migration): the popover shell (`SearchBarFilters`) below is
// superseded by `FiltersButton` in `@rubi/ui` (libs/ui/src/components/FiltersButton).
// Feedback already uses the shared component. Migrate these remaining
// consumers and then delete this file:
//   - apps/portal/src/components/alerts/Search/AlertFilters.tsx        (SearchBarFilters)
//   - apps/portal/src/components/alerts/AlertsList.tsx                 (RenderFilterChips / FilterChip)
//   - apps/portal/src/components/online-reporting/Search/ReportFilters.tsx
//                                                  (SearchBarFilters, DateRangePickerCustom,
//                                                   FilterChip, RenderFilterChips)
//   - apps/portal/src/components/online-reporting/ReportV2List.tsx     (RenderFilterChips)
//   - apps/portal/src/components/registry-system/RegistryEntries.tsx   (SearchBarFilters et al.)
// Plan: lift `FilterChip`, `RenderFilterChips`, and `DateRangePickerCustom`
// into `libs/ui/src/components/FiltersButton/` as sibling exports; swap each
// consumer's `<SearchBarFilters gridElements={...}>` for `<FiltersButton>` with
// children. See /Users/indeepfarma/.claude/plans/you-re-a-staff-senior-curious-sunbeam.md
// for the full follow-up checklist.

type FilterChipProps = {
  title: string;
  value?: string;
  onRemove: () => void;
  displayFullLabel?: boolean;
  customChipValueDisplay?: (value: string) => string;
  disableRemove?: boolean;
};

// Compute the text a FilterChip displays. Shared so collapsed chips can be
// listed in a tooltip without duplicating the formatting rules.
const formatChipLabel = ({
  title,
  value,
  displayFullLabel,
  customChipValueDisplay,
}: FilterChipProps) => {
  const display = customChipValueDisplay
    ? customChipValueDisplay(value ?? "")
    : (value ?? "");
  return displayFullLabel ? `${title}: ${display}` : display;
};

export const FilterChip = (props: FilterChipProps) => (
  <Chip
    label={formatChipLabel(props)}
    onDelete={props.disableRemove ? undefined : props.onRemove}
    color="secondary"
    variant="filled"
    // Truncate long labels so a single chip can't overflow on small screens.
    sx={{ maxWidth: { xs: 200, sm: 300 } }}
  />
);

/**
 * Renders a list of filter chips and a "Clear Filters" button.
 */
export const RenderFilterChips = ({
  filterChipsActive,
  onClearAll,
  hideClearAll,
}: {
  filterChipsActive: (JSX.Element | null)[];
  onClearAll: () => void;
  hideClearAll?: boolean;
}) => {
  // Cap visible chips so a long filter set can't overflow; collapse the rest
  // into a "+N" indicator that lists the hidden filters on hover.
  const maxVisibleChips = 7;

  const activeChips = filterChipsActive.filter(
    (filter): filter is JSX.Element => filter !== null,
  );
  if (activeChips.length === 0) return null;

  const visibleChips = activeChips.slice(0, maxVisibleChips);
  const hiddenChips = activeChips.slice(maxVisibleChips);
  const hiddenLabels = hiddenChips
    .map((chip) => {
      const props = chip.props as Partial<FilterChipProps> & {
        label?: unknown;
      };
      if (typeof props.title === "string") {
        return formatChipLabel(props as FilterChipProps) ?? "";
      }
      if (typeof props.label === "string") return props.label;
      return "";
    })
    .filter(Boolean)
    .join(", ");
  return (
    <Box
      sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}
    >
      {visibleChips}

      {hiddenChips.length > 0 && (
        <Tooltip title={hiddenLabels}>
          <Chip
            label={`+${hiddenChips.length}`}
            color="secondary"
            variant="outlined"
          />
        </Tooltip>
      )}

      {!hideClearAll && (
        <Button
          variant="outlined"
          color="secondary"
          onClick={onClearAll}
          size="small"
          sx={{
            borderRadius: 50,
          }}
        >
          Clear
        </Button>
      )}
    </Box>
  );
};

const maxVisibleGroupChips = 3;

export const FilterChipGroup = ({
  title,
  values,
  getLabel,
  getColor,
  onRemove,
  onClear,
}: {
  title: string;
  values: string[];
  getLabel: (value: string) => string;
  getColor?: (value: string) => ChipProps["color"];
  onRemove: (value: string) => void;
  onClear: () => void;
}) => {
  const hiddenValues = values.slice(maxVisibleGroupChips);
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: "center",
        px: 1,
        py: 0.5,
        bgcolor: "action.hover",
        borderRadius: "16px",
      }}
    >
      <Button
        size="small"
        onClick={onClear}
        color="secondary"
        startIcon={<Cancel />}
        aria-label={`Clear ${title}`}
        sx={{ minWidth: 0, padding: 0, margin: 0 }}
      />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {values.slice(0, maxVisibleGroupChips).map((value) => (
        <Chip
          key={value}
          label={getLabel(value)}
          size="small"
          color={getColor?.(value) ?? "default"}
          onDelete={() => onRemove(value)}
        />
      ))}
      {hiddenValues.length > 0 && (
        <Tooltip title={hiddenValues.map(getLabel).join(", ")}>
          <Chip
            label={`+${hiddenValues.length}`}
            size="small"
            variant="outlined"
          />
        </Tooltip>
      )}
    </Stack>
  );
};

export const DateRangePickerCustom = ({
  labelFrom,
  labelTo,
  startDate,
  endDate,
  onStartChange,
  onEndChange,
}: {
  labelFrom: string;
  labelTo: string;
  startDate: string;
  endDate: string;
  onStartChange: (newValue: string) => void;
  onEndChange: (newValue: string) => void;
}) => (
  <Grid container spacing={2}>
    <Grid size={{ xs: 6 }}>
      <DatePicker
        label={labelFrom}
        value={startDate ? dayjs(startDate) : null}
        onChange={(newValue) =>
          onStartChange(newValue?.toISOString().split("T")[0] || "")
        }
        slotProps={{
          textField: {
            variant: "outlined",
            fullWidth: true,
          },
          actionBar: {
            actions: ["today", "clear"],
          },
        }}
        maxDate={endDate ? dayjs(endDate) : undefined}
      />
    </Grid>
    <Grid size={{ xs: 6 }}>
      <DatePicker
        label={labelTo}
        value={endDate ? dayjs(endDate) : null}
        onChange={(newValue) =>
          onEndChange(newValue?.toISOString().split("T")[0] || "")
        }
        slotProps={{
          textField: { variant: "outlined", fullWidth: true },
          actionBar: {
            actions: ["today", "clear"],
          },
        }}
        minDate={startDate ? dayjs(startDate) : undefined}
      />
    </Grid>
  </Grid>
);

interface SearchBarFiltersProps {
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  gridElements: JSX.Element[];
}
export const SearchBarFilters = ({
  isOpen,
  anchorEl,
  onClose,
  gridElements,
}: SearchBarFiltersProps) => {
  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      sx={{ marginTop: 1 }}
    >
      <Grid
        container
        spacing={2}
        justifyContent="flex-start"
        alignItems="center"
        sx={{
          padding: 2,
          width: { xs: "100%", sm: anchorEl?.clientWidth || "auto" },
          marginBottom: 1,
        }}
      >
        {gridElements}
      </Grid>
    </Popover>
  );
};
